import { type FC, type ReactElement, useCallback } from "react"

import { QueryClient, useQueryClient } from "@tanstack/react-query"
import { AnimatePresence } from "framer-motion"
import { type NavigateFunction, useNavigate } from "react-router-dom"

import {
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    Typography
} from "@/3514/components"
import { participantSurveyQueryPrefetcher } from "@/entities/survey/api/use-respondent-survey"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { emptyAsyncCallback } from "@/shared/types/functions"

import { type TSurveysQuery, useSurveysQuery } from "../api"

import List from "./SurveysDataList"
import type { THandleListItemClickFunction } from "./types"
import { surveysPageUiUtils as utils } from "./utils"

import { SurveysAttentionBox as AttentionBox, SurveysFallbackSpinner as Spinner } from "."

const Container: FC = (): ReactElement => {
    const { data: surveysData, isFetching: isSurveysDataFetching }: TSurveysQuery = useSurveysQuery()

    const { t }: IUseTranslation = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const queryClient: QueryClient = useQueryClient()

    const handleListItemClick: THandleListItemClickFunction = useCallback(
        async (accessCode: string): Promise<void> =>
            accessCode
                ? void navigate(`/survey/${accessCode}/`, {
                      state: { shouldBackButtonRedirectToSurveysPage: true }
                  })
                : emptyAsyncCallback(),
        [navigate]
    )

    const handleListItemHover: THandleListItemClickFunction = useCallback(
        async (accessCode: string): Promise<void> => {
            if (!accessCode) return void 0

            return await participantSurveyQueryPrefetcher({ qc: queryClient }, { code: accessCode })
        },
        [queryClient]
    )

    const isAttentionBoxVisible: boolean = utils.getIsAttentionBoxVisible(surveysData?.results ?? [])

    return (
        <section className="surveys__container pb-[40px] md:pb-[100px]">
            <div className="surveys__heading md:pt-[40px]">
                <Typography
                    text={t("participantSide.surveysPage.heading")}
                    className="surveys__heading md:pt-[40px] m-0"
                    tag={ETypographyTag.Heading1}
                    color={ETypographyColor.Black}
                    alignment={ETypographyAlignment.Center}
                    weight={ETypographyWeight.Medium}
                    size={ETypographySize.Heading1}
                    family={ETypographyFamily.DmSerif}
                />
            </div>

            <div className="surveys__wrapper">
                {isAttentionBoxVisible && (
                    <>
                        <AttentionBox />
                        <hr className="separator" />
                    </>
                )}

                <AnimatePresence>
                    {isSurveysDataFetching || !surveysData?.results?.length ? (
                        <Spinner />
                    ) : (
                        <List
                            surveysData={surveysData.results}
                            handleListItemClick={handleListItemClick}
                            handleListItemHover={handleListItemHover}
                        />
                    )}
                </AnimatePresence>
            </div>
        </section>
    )
}

export { Container as SurveysContainer }
